// @flow
import React, { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { connect } from 'react-redux';
import { Router, Route, Redirect, Switch } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import PageLoader from '../components/PageLoader';
import { logoutAndClean } from '../utils/user';
import { IUser } from '../modules/account/models/user';
import { clearRedirect } from '../actions/session';
import { onkeyup, onkeydown } from '../actions/shortcut';
import Default from '../Layouts/Default';
import PrivateRoute from './PrivateRoute';
import ConversationPage from '../views/conversation/ConversationPage';
import ScribeLoginProfiling from '../modules/scribe/ScribeLoginProfilingContainer';

const ScribeWelcomeContainer = lazy(() => import('../modules/scribe/ScribeWelcomeContainer'));
const DownloadAppComponent = lazy(() => import('../components/DownloadAppComponent'));
const BranchRedirectScreen = lazy(() => import('../components/BranchRedirectScreen'));
const AppDownloadScreen = lazy(() => import('../components/AppDownloadScreen'));
const DesktopLoginSuccess = lazy(() => import('../components/DesktopLoginSuccess'));
const ScribeDashboardContainer = lazy(() => import('../modules/scribe/ScribeDashboard'));
const ScribeConversationsContainer = lazy(() => import('../modules/scribe/ScribeConversationsContainer'));
const Dashboard = lazy(() => import('../modules/account/pages/dashboard'));
const LicenceBuy = lazy(() => import('../modules/account/pages/license/buy'));
const Payment = lazy(() => import('../modules/account/pages/license/payment'));
const PaymentSucceed = lazy(() => import('../modules/account/pages/payment-succeed'));
const OrganizationCreate = lazy(() => import('../modules/account/pages/organization/create'));
const AccountStart = lazy(() => import('../modules/account/pages/start'));
const LinkBuilder = lazy(() => import('../modules/account/pages/linkbuilder'));
const AccountSettings = lazy(() => import('../modules/account-settings/AccountSettings'));

type Props = {
  AppWrapper: Function,
  accountUser: IUser,
  avaId: string,
  captioner: Object,
  clearRedirect: Function,
  features: Object,
  onkeyup: Function,
  onkeydown: Function,
  redirectTo: { pathname: string, search?: string | typeof undefined } | typeof undefined,
  token: string,
};

const browserHistory = createBrowserHistory();

Sentry.init({
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(browserHistory),
    }),
  ],
  tracesSampleRate: 1.0,
  ignoreErrors: ['connection appears to be offline'],
});

window.browserHistory = browserHistory;

class Routes extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    const { redirectTo: oldRedirectTo } = this.props;
    return nextProps.redirectTo !== oldRedirectTo;
  }

  componentDidMount() {
    // eslint-disable-next-line
    const { onkeydown, onkeyup } = this.props;
    window.document.onkeydown = onkeydown;
    window.document.onkeyup = onkeyup;
  }

  render() {
    // eslint-disable-next-line
    const { clearRedirect, redirectTo, AppWrapper } = this.props;

    if (redirectTo) {
      // clearRedirect causes error "Cannot update component while rendering another component."
      // we delay it with setTimeout
      setTimeout(clearRedirect);
      return (
        <Router history={browserHistory}>
          <Redirect
            to={{
              ...redirectTo,
              search: redirectTo.search ?? window.location.search,
            }}
          />
        </Router>
      );
    }

    const scribeAuthenticate = () => {
      const { captioner, features } = this.props;
      return captioner && features && features.scribe;
    };
    const webAppAuthenticate = () => {
      const { avaId } = this.props;
      return avaId && avaId !== '';
    };
    const accountAuthenticate = () => {
      const { accountUser } = this.props;
      return accountUser && !!accountUser.id;
    };

    const isAnonymous = localStorage.getItem('isAnonymous');
    return (
      <div id="gesture">
        <Router history={browserHistory}>
          <AppWrapper>
            <Switch>
              <Route
                path="/branch/:branchUrlId"
                render={props => (
                  <Suspense fallback={<PageLoader />}>
                    <BranchRedirectScreen {...props} />
                  </Suspense>
                )}
              />
              <Route
                path="/download"
                render={props => (
                  <Suspense fallback={<PageLoader />}>
                    <AppDownloadScreen {...props} />
                  </Suspense>
                )}
              />
              <Route path="/">
                <Default>
                  <Suspense fallback={<PageLoader />}>
                    <Switch>
                      <Route path="/account">
                        <Switch>
                          <Route exact path="/account">
                            <Redirect to="/account/dashboard" />
                          </Route>
                          <Route path="/account/start" exact>
                            <AccountStart />
                          </Route>
                          <Route path="/account/login/profile" render={() => <ScribeLoginProfiling />} />
                          <Route
                            render={props => <ScribeWelcomeContainer {...props} product="account" />}
                            path="/account/login"
                          />
                          <Route path="/account/linkbuilder" exact>
                            <LinkBuilder />
                          </Route>
                          <PrivateRoute
                            path="/account/payment-succeed"
                            component={PaymentSucceed}
                            authenticate={accountAuthenticate}
                            redirect="/account/dashboard"
                          />
                          <PrivateRoute
                            path="/account/organization/create"
                            component={OrganizationCreate}
                            authenticate={accountAuthenticate}
                            redirect="/account/dashboard"
                          />
                          <Route path="/account/dashboard" render={props => <Dashboard {...props} />} />
                          <PrivateRoute
                            path="/account/license/buy"
                            component={LicenceBuy}
                            authenticate={accountAuthenticate}
                            redirect="/account/dashboard"
                            keepPath={true}
                          />
                          <PrivateRoute
                            path="/account/license/payment"
                            component={Payment}
                            authenticate={accountAuthenticate}
                            redirect="/account/dashboard"
                          />
                        </Switch>
                      </Route>
                      <Route path="/web">
                        <Switch>
                          <Route path="/web/login/profile" render={() => <ScribeLoginProfiling />} />
                          <Route
                            exact
                            render={props => <ScribeWelcomeContainer {...props} product="web" />}
                            path="/web"
                          />
                          <Route
                            path="/web/login/desktop"
                            render={props => <DesktopLoginSuccess {...props} product="web" />}
                          />
                          <Route
                            exact
                            render={props => {
                              return isAnonymous && isAnonymous === 'true' ? (
                                <Redirect to="/web" />
                              ) : (
                                <ScribeWelcomeContainer {...props} product="web" />
                              );
                            }}
                            path="/web/login"
                          />
                          <Route
                            render={props => <ScribeWelcomeContainer {...props} product="web" />}
                            path="/web/login"
                          />
                          <Route
                            render={() => {
                              logoutAndClean();
                              return <Redirect to="/web/login" />;
                            }}
                            path="/web/logout"
                          />
                          <PrivateRoute
                            path="/web/scribe-dashboard"
                            component={ScribeDashboardContainer}
                            authenticate={webAppAuthenticate}
                            redirect="/web"
                          />
                          <PrivateRoute
                            path="/web/conversations"
                            component={ScribeConversationsContainer}
                            authenticate={webAppAuthenticate}
                            redirect="/web"
                          />
                          <PrivateRoute
                            path="/web/transcript"
                            component={ConversationPage}
                            authenticate={webAppAuthenticate}
                            redirect="/web"
                          />
                          <PrivateRoute
                            path="/web/account-settings"
                            component={AccountSettings}
                            authenticate={webAppAuthenticate}
                            redirect="/web"
                          />
                        </Switch>
                      </Route>
                      <Route path="/">
                        <Switch>
                          <Route exact component={DownloadAppComponent} path="/download" />
                          <Redirect to="/web" />
                        </Switch>
                      </Route>
                      <Redirect from="*" to="/web" />
                    </Switch>
                  </Suspense>
                </Default>
              </Route>
            </Switch>
          </AppWrapper>
        </Router>
      </div>
    );
  }
}

const mapStateToProps = ({
  scribeSession: { captioner, features, avaId, redirectTo },
  scribeConversation: { token },
  account: {
    user: { user: accountUser },
  },
  currentUser,
}) => ({
  avaId: (captioner || {}).avaId || avaId,
  currentUser,
  captioner,
  features,
  token,
  accountUser,
  redirectTo,
});
export default connect(mapStateToProps, {
  onkeydown,
  onkeyup,
  clearRedirect,
})(Routes);
